// src/services/http/client.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { authStorage } from '@/services/auth/storage';

// Configuración base de la API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://eisegmiventas.facturador.es';

// Cliente HTTP base
export class HttpClient {
    private client: AxiosInstance;
    private isRefreshing: boolean = false;
    private failedQueue: Array<{
        resolve: (value?: any) => void;
        reject: (reason?: any) => void;
    }> = [];

    constructor(baseURL: string = `${API_BASE_URL}/api/v1`) {
        this.client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 30000, // 30 segundos
        });

        this.setupInterceptors();
    }

    private setupInterceptors(): void {
        // Request interceptor - agregar token de autenticación
        this.client.interceptors.request.use(
            (config) => {
                const token = authStorage.getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor - manejar errores y refresh de tokens
        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Si es error 401 y no hemos intentado refrescar el token
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // Si ya estamos refrescando tokens, poner en cola
                    if (this.isRefreshing) {
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({ resolve, reject });
                        }).then(token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return this.client(originalRequest);
                        }).catch(err => {
                            return Promise.reject(err);
                        });
                    }

                    this.isRefreshing = true;

                    try {
                        const refreshToken = authStorage.getRefreshToken();
                        if (!refreshToken) {
                            throw new Error('No refresh token available');
                        }

                        // SOLUCIÓN: Crear una instancia separada para el refresh
                        // para evitar que pase por el interceptor
                        const refreshClient = axios.create({
                            baseURL: this.client.defaults.baseURL,
                            timeout: 30000,
                        });

                        const response = await refreshClient.post('/auth/refresh', {
                            refresh_token: refreshToken,
                        });

                        const { access_token, refresh_token: newRefreshToken } = response.data.data;

                        // Guardar nuevos tokens
                        authStorage.setTokens(access_token, newRefreshToken);

                        // Procesar cola de peticiones fallidas
                        this.processQueue(null, access_token);

                        // Reintentar la petición original
                        originalRequest.headers.Authorization = `Bearer ${access_token}`;
                        return this.client(originalRequest);

                    } catch (refreshError) {
                        // Si falla el refresh, limpiar tokens y procesar cola con error
                        this.processQueue(refreshError, null);
                        authStorage.clearTokens();

                        // Redirigir al login
                        window.location.href = '/login';
                        return Promise.reject(refreshError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    private processQueue(error: any, token: string | null = null): void {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });

        this.failedQueue = [];
    }

    // Métodos HTTP básicos
    async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.get<T>(url, config);
    }

    async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.post<T>(url, data, config);
    }

    async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.put<T>(url, data, config);
    }

    async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.patch<T>(url, data, config);
    }

    async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.delete<T>(url, config);
    }

    // Método para upload de archivos
    async upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.post<T>(url, formData, {
            ...config,
            headers: {
                ...config?.headers,
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    // Obtener la instancia de axios para casos especiales
    getInstance(): AxiosInstance {
        return this.client;
    }
}

// Instancia singleton del cliente HTTP
export const httpClient = new HttpClient();